import styled, { css, keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";
import { toneColor } from "../../../../../utils";
import { EarnedTone } from "./types";
import { BaseButton } from "../../../../../components/Button";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Bars grow from their anchored edge on entrance via a composited transform,
// never an animated width (layout property). Ends at scaleX(1) so the pill caps
// rest undistorted.
const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }

  margin-bottom: 0;
`;

export const Header = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  width: 100%;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
  color: var(--color-text-default);
  cursor: pointer;
  text-align: left;
  transition: ${ANIMATION.transitions.all};

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-secondary);
  transition: transform ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "180deg")});

  & > svg {
    font-size: var(--text-xl);
  }
`;

export const HeaderTitle = styled.span`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const HeaderSubtitle = styled.span`
  font-size: var(--text-base);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const CollapseLabel = styled.span`
  margin-left: auto;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.xl}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  animation: ${fadeIn} ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${SPACING.xxl}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  min-width: 0;
`;

export const ColumnHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const ColumnTitle = styled.h3`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const ColumnSubtitle = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const ColumnEmpty = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const CompactSubtitle = styled(ColumnSubtitle)`
  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

const EARNED_COLUMNS = "150px minmax(0, 1fr) 88px 64px 88px";

export const EarnedLegend = styled.div`
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: grid;
    grid-template-columns: ${EARNED_COLUMNS};
    align-items: baseline;
    gap: ${SPACING.md}px;
  }
`;

export const LegendSubtitle = styled(ColumnSubtitle)`
  grid-column: 1 / 3;
`;

export const LegendLabel = styled.span`
  justify-self: end;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const RowList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Row = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "name value"
    "track track";
  align-items: center;
  gap: ${SPACING.xs}px ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: 150px minmax(0, 1fr) 88px;
    grid-template-areas: "name track value";
    gap: ${SPACING.md}px;
  }
`;

export const EarnedRow = styled(Row)`
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "name value"
    "track track"
    "tax net";
  font-variant-numeric: tabular-nums lining-nums;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: ${EARNED_COLUMNS};
    grid-template-areas: "name track value tax net";
  }
`;

export const Name = styled.span`
  grid-area: name;
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-default);

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Swatch = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

export const Value = styled.span<{ $tone?: EarnedTone }>`
  grid-area: value;
  justify-self: end;
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  color: ${({ $tone }) =>
    $tone ? toneColor($tone) : "var(--color-text-default)"};
`;

const breakdownCell = css`
  margin-top: ${SPACING.xxs}px;
  font-size: var(--text-sm);
  white-space: nowrap;
  color: var(--color-text-secondary);

  @media ${QUERIES.tabletLargeAndUp} {
    justify-self: end;
    margin-top: 0;
    font-size: var(--text-base);
  }
`;

export const Tax = styled.span`
  grid-area: tax;
  ${breakdownCell}
`;

export const Net = styled.span`
  grid-area: net;
  justify-self: end;
  ${breakdownCell}
`;

export const NetValue = styled.span`
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const CompactLabel = styled.span`
  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const Track = styled.div`
  grid-area: track;
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: var(--color-background-3);
  overflow: hidden;
`;

export const AllocationFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $color }) => $color};
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const EarnedFill = styled.div<{ $width: number; $tone: EarnedTone }>`
  height: 100%;
  border-radius: 999px;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $tone }) => toneColor($tone)};
  transform-origin: left center;
  animation: ${growX} ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
